import { mapWith, mapWithout } from '@brainfocus/web/shared/collections/maps';
import { assertValue } from '@brainfocus/web/utils/assert';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import './survey.scss';
import { QuestionReference, SurveyAnswer, SurveyQuestion, SurveyResult, UserInputAnswer } from '@brainfocus/web/domain/survey/survey_format';
import { TextInput } from '@brainfocus/web/components/text_input/text_input';
import { useEventCallback } from '@brainfocus/web/utils/react/use_event_callback';

export function Survey({ survey, onUpdate }: {
    survey: SurveyQuestion[];
    onUpdate?: (s: SurveyState) => void;
}) {
    const [answers, setAnswers] = useState<ReadonlyMap<string, number | string | number[]>>(new Map());
    const renderedQuestions = useRef(new Set<string>());
    const optionalQuestions = useRef(new Set<string>());
    renderedQuestions.current.clear();
    optionalQuestions.current.clear();

    const questionMap = useMemo<ReadonlyMap<string, SurveyQuestion>>(() => {
        const allQuestions = survey ? flattenSurveyQuestions(survey) : [];
        const entries = allQuestions
            .filter(q => q.id)
            .map(q => [q.id!, q] as [string, SurveyQuestion]);
        return new Map(entries);
    }, [survey]);

    console.log(Object.fromEntries(answers));

    const notifyUpdate = useEventCallback((state: SurveyState) => onUpdate?.(state));

    useEffect(() => {
        const completed = [...renderedQuestions.current].every((v) => {
            // Skip optional questions in completion check
            if (optionalQuestions.current.has(v)) return true;
            const answer = answers.get(v);
            if (answer === undefined) return false;
            // For multi-select (arrays), require at least one selection
            if (Array.isArray(answer)) return answer.length > 0;
            return true;
        });
        notifyUpdate({
            completed,
            answers: Object.fromEntries(answers),
        });
    }, [answers, notifyUpdate]);

    useEffect(() => {
        const posted: [string, number | string][] = [];

        ([...document.querySelectorAll(`meta`)] as HTMLMetaElement[])
            .filter(meta => meta.name.startsWith('post:q_'))
            .forEach((meta) => {
                const parsed = parseInt(meta.content);
                if (parsed.toString() === meta.content) {
                    posted.push([meta.name.replace('post:', ''), parsed]);
                } else {
                    posted.push([meta.name.replace('post:', ''), meta.content]);
                }
            });
        setAnswers(prev => posted.reduce((outA, [key, value]) => mapWith(outA, key, value), prev));
    }, [survey]);

    let qNumber = 1;
    // Preact is derping here unless each element is wrapped like this
    return <>{[...renderQuestion(survey[0]!, 'q_0')].map(v => <>{v}</>)}</>;

    function * renderQuestion(question: SurveyQuestion | QuestionReference, qPath: string): Iterable<JSX.Element> {
        if ('refId' in question) {
            question = assertValue(
                questionMap.get(question.refId),
                `Non-existent question ${question.refId}`,
            );
        }
        let questionHolders: (SurveyAnswer | UserInputAnswer)[];
        renderedQuestions.current.add(qPath);
        if (question.optional) {
            optionalQuestions.current.add(qPath);
        }
        if (question.text) {
            yield <p class='bf-survey-question'>{qNumber++}. {question.text}{question.optional && <span class='text-muted'> (Optional)</span>}</p>;
        } else {
            yield <div class='mb-1'></div>;
        }
        if (question.answers instanceof Array) {
            if (question.multiSelect) {
                // Multi-select: render checkboxes
                const currentSelected = answers.get(qPath);
                const selectedArray = Array.isArray(currentSelected) ? currentSelected : [];

                for (const [answerI, answer] of question.answers.entries()) {
                    yield (
                        <label class='bf-survey-answer'>
                            <input
                                type='checkbox'
                                name={`${qPath}_${answerI}`}
                                onInput={() => {
                                    const newSelected = selectedArray.includes(answerI)
                                        ? selectedArray.filter(i => i !== answerI)
                                        : [...selectedArray, answerI].sort((a, b) => a - b);
                                    setAnswers(mapWith(answers, qPath, newSelected));
                                }}
                                checked={selectedArray.includes(answerI)}
                                value={answerI}
                            />
                            {' '}
                            {answer.text}
                        </label>
                    );
                }
            } else {
                // Single-select: render radio buttons
                for (const [answerI, answer] of question.answers.entries()) {
                    yield (
                        <label class='bf-survey-answer'>
                            <input
                                type='radio'
                                name={qPath}
                                onInput={() => setAnswers(mapWith(answers, qPath, answerI))}
                                checked={answers.get(qPath) === answerI}
                                value={answerI}
                            />
                            {' '}
                            {answer.text}
                        </label>
                    );
                }
            }
            questionHolders = question.answers;
        } else {
            yield <TextInput<string>
                name={qPath}
                onChange={(v) => {
                    if (typeof v === 'string') {
                        setAnswers(mapWith(answers, qPath, v));
                    } else {
                        setAnswers(mapWithout(answers, qPath));
                    }
                }}
                output='string'
                label='Answer'
                required={!question.optional}
                  />;
            questionHolders = [question.answers];
        }

        const currentAnswer = answers.get(qPath);
        for (const [answerI, answer] of questionHolders.entries()) {
            const isSelected = currentAnswer === answerI
                || (typeof currentAnswer === 'string' && currentAnswer)
                || (Array.isArray(currentAnswer) && currentAnswer.includes(answerI));
            if (isSelected && answer.questions) {
                for (const [subQuestI, subQuest] of answer.questions.entries()) {
                    yield * renderQuestion(subQuest, `${qPath}_${answerI}_${subQuestI}`);
                }
            }
        }
    }
}

function flattenSurveyQuestions(qs: (SurveyQuestion | QuestionReference)[]): SurveyQuestion[] {
    return qs.flatMap((q) => {
        if ('answers' in q) {
            const sub = q.answers instanceof Array
                ? q.answers.flatMap((a) => {
                    return a.questions ? flattenSurveyQuestions(a.questions) : [];
                })
                : [];
            return [q, ...sub];
        } else {
            return [];
        }
    });
}

export interface SurveyState {
    completed: boolean;
    answers: SurveyResult;
}
